import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { playChime, showNotification } from '../utils/notify';
import { shouldChimeNewEdgeMessage } from '../utils/liveChatChime';

// Owns the front-desk side of the Modality ↔ Jonathan live chat:
// session + messages + loading flag, the "new reply since you last
// looked" unread flag, the right-edge rail's open/close state, and the
// polling pipeline that keeps all of it fresh.
//
// Effects owned:
//   * viewingFdLiveChatRef sync — derived from drawer + tab + rail so
//     refreshFdLiveChat can tell "is the user actually looking at this
//     chat right now?" without closing over the booleans.
//   * Clear fdLiveChatHasUnread whenever the user starts viewing.
//   * Jonathan-offline reset + poll setup: when he goes offline we
//     wipe session/messages/unread and force the rail closed; while
//     he is online/busy we poll every 4s (visible tab) / 30s (hidden
//     tab), flipping the interval on visibilitychange.
//   * Escape closes the rail.
//
// refreshFdLiveChat has empty deps and reads viewingFdLiveChatRef
// instead of the boolean triple so it stays stable and the poll-setup
// effect doesn't tear down on every drawer toggle.
//
// Consumes `jonathanStatus` + the two support-drawer booleans as args.
// The parent still owns those (jonathanStatus moves into useSupport in
// 6e; supportDrawerOpen / liveChatOpen stay where they are).
export function useFdLiveChat({ jonathanStatus, supportDrawerOpen, liveChatOpen }) {
  const [fdLiveChatSession, setFdLiveChatSession] = useState(null);
  const [fdLiveChatMessages, setFdLiveChatMessages] = useState([]);
  const [fdLiveChatLoading, setFdLiveChatLoading] = useState(false);
  const [fdLiveChatHasUnread, setFdLiveChatHasUnread] = useState(false);
  const [liveChatRailOpen, setLiveChatRailOpen] = useState(false);

  const prevFdLiveChatMsgLenRef = useRef(0);
  const viewingFdLiveChatRef = useRef(false);

  useEffect(() => {
    viewingFdLiveChatRef.current =
      (supportDrawerOpen && liveChatOpen) || liveChatRailOpen;
  }, [supportDrawerOpen, liveChatOpen, liveChatRailOpen]);

  useEffect(() => {
    if ((supportDrawerOpen && liveChatOpen) || liveChatRailOpen) {
      setFdLiveChatHasUnread(false);
    }
  }, [supportDrawerOpen, liveChatOpen, liveChatRailOpen]);

  const refreshFdLiveChat = useCallback(async () => {
    try {
      const res = await fetch('/api/support/live-chat/session');
      const j = await res.json();
      if (!res.ok || !j?.ok) return;
      const sess = j.session || null;
      const msgs = Array.isArray(j.messages) ? j.messages : [];
      const prevLen = prevFdLiveChatMsgLenRef.current;
      const viewing = viewingFdLiveChatRef.current;
      if (!viewing && shouldChimeNewEdgeMessage(prevLen, msgs, 'jonathan')) {
        setFdLiveChatHasUnread(true);
        try {
          playChime();
        } catch { /* ignore */ }
        showNotification('Jonathan (Live Chat)', {
          body: 'New reply — tap to open.',
          tag: 'fd-live-chat-reply',
          onClick: () => setLiveChatRailOpen(true),
        });
      }
      prevFdLiveChatMsgLenRef.current = msgs.length;
      if (!sess) {
        prevFdLiveChatMsgLenRef.current = 0;
        setFdLiveChatHasUnread(false);
      }
      setFdLiveChatSession(sess);
      setFdLiveChatMessages(msgs);
      setFdLiveChatLoading(false);
    } catch {
      setFdLiveChatLoading(false);
    }
  }, []);

  useEffect(() => {
    if (jonathanStatus === 'offline') {
      setFdLiveChatSession(null);
      setFdLiveChatMessages([]);
      setFdLiveChatHasUnread(false);
      prevFdLiveChatMsgLenRef.current = 0;
      setLiveChatRailOpen(false);
      setFdLiveChatLoading(false);
      return undefined;
    }
    setFdLiveChatLoading(true);
    refreshFdLiveChat();
    let t = setInterval(refreshFdLiveChat, document.hidden ? 30000 : 4000);
    const onVisibility = () => {
      clearInterval(t);
      t = setInterval(refreshFdLiveChat, document.hidden ? 30000 : 4000);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisibility); };
  }, [jonathanStatus, refreshFdLiveChat]);

  useEffect(() => {
    if (!liveChatRailOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLiveChatRailOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [liveChatRailOpen]);

  const fdLiveChatShared = useMemo(
    () =>
      jonathanStatus === 'online' || jonathanStatus === 'busy'
        ? {
            messages: fdLiveChatMessages,
            session: fdLiveChatSession,
            isLoading: fdLiveChatLoading,
            refresh: refreshFdLiveChat,
          }
        : null,
    [jonathanStatus, fdLiveChatMessages, fdLiveChatSession, fdLiveChatLoading, refreshFdLiveChat]
  );

  return {
    fdLiveChatSession,
    fdLiveChatMessages,
    fdLiveChatLoading,
    fdLiveChatHasUnread,
    setFdLiveChatHasUnread,
    liveChatRailOpen,
    setLiveChatRailOpen,
    refreshFdLiveChat,
    fdLiveChatShared,
  };
}
